import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { PrivacyAgreementPopupComponent } from '../privacy-agreement-popup/privacy-agreement-popup.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, PrivacyAgreementPopupComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  showPassword = false;
  showShakeAnimation = false;
  returnUrl: string = '/dashboard';
  showPrivacyAgreement = false;
  requiresPrivacyAgreement = false;
  showReferralCodes = false; // Toggle for showing referral code fields
  parentReferralCode = '';
  spouseReferralCode = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false],
      parentReferralCode: [''], // Optional referral codes for Google sign-up
      spouseReferralCode: ['']
    });
  }

  ngOnInit(): void {
    // Check if user is already logged in
    if (this.authService.isAuthenticated()) {
      // Redirect based on user role
      if (this.authService.isAdmin()) {
        this.router.navigate(['/dashboard']);
      } else {
        this.router.navigate(['/home']);
      }
      return;
    }

    // Get return URL and reason from route parameters
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
    const reason = this.route.snapshot.queryParams['reason'];
    
    // Get referral codes from query params
    this.parentReferralCode = this.route.snapshot.queryParams['parentReferralCode'] || '';
    this.spouseReferralCode = this.route.snapshot.queryParams['spouseReferralCode'] || '';
    
    // Pre-fill form if referral codes are in URL
    if (this.parentReferralCode || this.spouseReferralCode) {
      this.loginForm.patchValue({
        parentReferralCode: this.parentReferralCode,
        spouseReferralCode: this.spouseReferralCode
      });
      this.showReferralCodes = true; // Show the fields if codes are in URL
    }
    
    // Show appropriate message based on redirect reason
    if (reason === 'session_expired') {
      this.errorMessage = 'Your session has expired. Please log in again.';
    } else if (reason === 'no_token') {
      this.errorMessage = 'Please log in to access this page.';
    }

    // Initialize Google Sign-In - retry if script not loaded
    this.initializeGoogleSignIn();
  }

  initializeGoogleSignIn(retryCount = 0): void {
    const maxRetries = 20; // Increased retries
    const retryDelay = 300; // Increased delay

    // Check if Google script is loaded and Client ID is configured
    if (typeof (window as any).google !== 'undefined' && 
        typeof (window as any).google.accounts !== 'undefined' &&
        typeof (window as any).google.accounts.id !== 'undefined' &&
        environment.googleClientId && 
        environment.googleClientId !== 'YOUR_GOOGLE_CLIENT_ID_HERE') {
      
      try {
        const buttonElement = document.getElementById('google-signin-button');
        if (!buttonElement) {
          console.error('Google sign-in button element not found');
          if (retryCount < maxRetries) {
            setTimeout(() => this.initializeGoogleSignIn(retryCount + 1), retryDelay);
          }
          return;
        }

        // Initialize Google Sign-In
        (window as any).google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: (response: any) => this.handleGoogleSignIn(response),
          auto_select: false,
          cancel_on_tap_outside: true
        });

        // Clear loading message
        buttonElement.innerHTML = '';

        // Render the button
        (window as any).google.accounts.id.renderButton(
          buttonElement,
          {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: 'signin_with',
            locale: 'en'
          }
        );
        
        console.log('Google Sign-In button initialized successfully');
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
        // Retry on error
        if (retryCount < maxRetries) {
          setTimeout(() => this.initializeGoogleSignIn(retryCount + 1), retryDelay);
        } else {
          const buttonElement = document.getElementById('google-signin-button');
          if (buttonElement) {
            buttonElement.innerHTML = '<p style="color: #64748b; font-size: 14px; text-align: center; padding: 12px;">Google Sign-In unavailable</p>';
          }
        }
      }
    } else if (retryCount < maxRetries) {
      // Retry if script not loaded yet
      setTimeout(() => this.initializeGoogleSignIn(retryCount + 1), retryDelay);
    } else {
      console.warn('Google Sign-In script failed to load after multiple retries');
      console.warn('Google object:', typeof (window as any).google);
      console.warn('Google Client ID:', environment.googleClientId);
      // Show a fallback message or button if needed
      const buttonElement = document.getElementById('google-signin-button');
      if (buttonElement) {
        buttonElement.innerHTML = '<p style="color: #64748b; font-size: 14px; text-align: center; padding: 12px;">Google Sign-In unavailable</p>';
      }
    }
  }

  handleGoogleSignIn(response: any): void {
    if (!response.credential) {
      this.errorMessage = 'Google sign-in failed. Please try again.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    // Get referral codes from form or query params
    const parentReferralCode = this.loginForm.get('parentReferralCode')?.value?.trim() || 
                                this.parentReferralCode || 
                                this.route.snapshot.queryParams['parentReferralCode'];
    const spouseReferralCode = this.loginForm.get('spouseReferralCode')?.value?.trim() || 
                               this.spouseReferralCode || 
                               this.route.snapshot.queryParams['spouseReferralCode'];

    const googleAuthRequest = {
      idToken: response.credential,
      parentReferralCode: parentReferralCode || undefined,
      spouseReferralCode: spouseReferralCode || undefined,
      acceptPrivacyAgreement: true // Assume privacy agreement accepted for Google sign-in
    };

    console.log('🔐 Initiating Google authentication...');

    this.authService.googleAuth(googleAuthRequest).subscribe({
      next: (authResponse: any) => {
        console.log('✅ Google authentication successful:', authResponse);
        
        // Verify response structure
        if (!authResponse || !authResponse.success) {
          console.error('❌ Invalid response structure:', authResponse);
          this.isLoading = false;
          this.errorMessage = 'Authentication failed. Invalid response from server.';
          return;
        }

        // Check if privacy agreement is required
        if (authResponse.data?.requiresPrivacyAgreement) {
          console.log('⚠️ Privacy agreement required');
          this.isLoading = false;
          this.requiresPrivacyAgreement = true;
          this.showPrivacyAgreement = true;
          return;
        }

        // Verify token is stored and authentication state is ready
        const verifyAuth = () => {
          const token = this.authService.getToken();
          const user = this.authService.getCurrentUser();
          const isAuthenticated = this.authService.isAuthenticated();
          
          console.log('🔑 Token stored:', !!token);
          console.log('👤 User stored:', !!user);
          console.log('🔐 Is authenticated:', isAuthenticated);
          
          return token && user && isAuthenticated;
        };

        // Wait for auth state to be ready, with retries
        let retries = 0;
        const maxRetries = 10;
        const checkAndNavigate = () => {
          if (verifyAuth() || retries >= maxRetries) {
            this.isLoading = false;
            
            if (!verifyAuth()) {
              console.error('❌ Auth state verification failed after retries');
              this.errorMessage = 'Authentication successful but state verification failed. Please refresh the page.';
              return;
            }
            
            // Determine destination
            let destination = '/home';
            if (authResponse.data && authResponse.data.isAdmin) {
              destination = '/dashboard';
              console.log('🚀 Redirecting to admin dashboard');
            } else if (this.returnUrl && this.returnUrl !== '/login' && this.returnUrl !== '/admin' && this.returnUrl !== '/dashboard') {
              destination = this.returnUrl;
              console.log('🚀 Redirecting to return URL:', this.returnUrl);
            } else {
              console.log('🚀 Redirecting to home');
            }
            
            // Navigate using navigateByUrl for more explicit navigation
            console.log('🔀 Attempting router navigation to:', destination);
            this.router.navigateByUrl(destination, { skipLocationChange: false })
              .then(success => {
                if (success) {
                  console.log('✅ Router navigation successful to:', destination);
                  // Navigation successful, no need for fallback
                } else {
                  console.warn('⚠️ Router navigation returned false, using fallback');
                  // Fallback: force navigation with page reload only if router fails
                  setTimeout(() => {
                    console.log('🔄 Forcing navigation via window.location.href');
                    window.location.href = destination;
                  }, 100);
                }
              })
              .catch(err => {
                console.error('❌ Router navigation error:', err);
                // Fallback: force navigation with page reload on error
                console.log('🔄 Using fallback navigation to:', destination);
                setTimeout(() => {
                  window.location.href = destination;
                }, 100);
              });
          } else {
            retries++;
            setTimeout(checkAndNavigate, 50);
          }
        };
        
        // Start checking after a brief delay
        setTimeout(checkAndNavigate, 50);
      },
      error: (error) => {
        this.isLoading = false;
        console.error('❌ Google authentication error:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          error: error.error
        });
        
        if (error.status === 401 || error.status === 403) {
          this.errorMessage = error.error?.message || 'Authentication failed. Please check your Google account.';
        } else if (error.status === 0) {
          this.errorMessage = 'Unable to connect to server. Please check your internet connection.';
        } else {
          this.errorMessage = error.error?.message || 'Google sign-in failed. Please try again.';
        }
      }
    });
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      this.showShakeAnimation = false;

      const credentials = {
        username: this.loginForm.value.username,
        password: this.loginForm.value.password,
        rememberMe: this.loginForm.value.rememberMe
      };

      this.authService.login(credentials).subscribe({
        next: (response) => {
          this.isLoading = false;
          
          
          // Store remember me preference
          if (credentials.rememberMe) {
            localStorage.setItem('rememberMe', 'true');
          } else {
            localStorage.removeItem('rememberMe');
          }

          // Check if privacy agreement is required
          if (response.data?.requiresPrivacyAgreement) {
            this.requiresPrivacyAgreement = true;
            this.showPrivacyAgreement = true;
            return;
          }

          // Navigate based on user role first, then return URL
          if (response.data && response.data.isAdmin) {
            this.router.navigate(['/dashboard']);
          } else if (this.returnUrl && this.returnUrl !== '/login' && this.returnUrl !== '/admin' && this.returnUrl !== '/dashboard') {
            this.router.navigate([this.returnUrl]);
          } else {
            this.router.navigate(['/home']);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.showShakeAnimation = true;
          
          // Reset shake animation after it completes
          setTimeout(() => {
            this.showShakeAnimation = false;
          }, 500);

          // Handle different error types
          if (error.status === 401) {
            this.errorMessage = 'Invalid username or password. Please try again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Account is locked. Please contact support.';
          } else if (error.status === 0) {
            this.errorMessage = 'Unable to connect to server. Please check your internet connection.';
          } else {
            this.errorMessage = error.error?.message || 'Login failed. Please try again.';
          }
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.loginForm.markAllAsTouched();
      this.showShakeAnimation = true;
      
      setTimeout(() => {
        this.showShakeAnimation = false;
      }, 500);
    }
  }

  // Demo login for testing
  demoLogin(): void {
    this.loginForm.patchValue({
      username: 'demo@familitive.com',
      password: 'demo123'
    });
    this.onSubmit();
  }

  onPrivacyAgreementAccepted(): void {
    this.showPrivacyAgreement = false;
    this.requiresPrivacyAgreement = false;
    
    // Navigate based on user role
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.isAdmin) {
      this.router.navigate(['/dashboard']);
    } else if (this.returnUrl && this.returnUrl !== '/login' && this.returnUrl !== '/admin' && this.returnUrl !== '/dashboard') {
      this.router.navigate([this.returnUrl]);
    } else {
      this.router.navigate(['/home']);
    }
  }

  onPrivacyAgreementRejected(): void {
    this.showPrivacyAgreement = false;
    this.authService.logout();
    this.errorMessage = 'You must accept the privacy agreement to use this service.';
  }

  toggleReferralCodes(): void {
    this.showReferralCodes = !this.showReferralCodes;
  }
}
